import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { setLogin, setLogout } from '../../state';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGoogle, FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { jwtDecode } from "jwt-decode";

// Simplified login hook without dependencies on socket or state clearing
export function useHandleLogin(dispatch) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);
    
    // Get form data
    const formData = new FormData(event.target);
    const email = formData.get('email');
    const password = formData.get('password');
    const rememberMe = formData.get('rememberMe') === 'on';

    // Validate inputs
    if (!email || !password) {
      setError('Email and password are required');
      setIsLoading(false);
      return;
    }

    try {
      // Make login request
      const response = await fetch(`${process.env.REACT_APP_AUTH_API_URL}/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Api-Name': 'auth-api',
        },
        body: JSON.stringify({ email, password })
      });

      // Parse response
      const data = await response.json();

      // Handle error
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Handle success
      if (data.user && data.token) {
        // Handle remember me - with try/catch for Safari
        try {
          if (rememberMe) {
            localStorage.setItem('rememberedEmail', email);
            localStorage.setItem('rememberedPassword', password);
          } else {
            localStorage.removeItem('rememberedEmail');
            localStorage.removeItem('rememberedPassword');
          }
        } catch (e) {
          // Continue even if localStorage fails
        }

        // Dispatch login action
        dispatch(setLogin({ user: data.user, token: data.token }));
        
        // Add a small delay to ensure state is updated before navigation
        setTimeout(() => {
          navigate('/home');
        }, 500);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      // Provide more meaningful error messages based on the error type
      if (error.message === 'Failed to fetch') {
        setError('Unable to connect to the server. Try again later.');
      } else if (error.message.includes('NetworkError') || error.message.includes('Network request failed')) {
        setError('Network error occurred. Please check your connection and try again.');
      } else if (error.message.includes('timeout')) {
        setError('Request timed out. Please try again later.');
      } else if (error.message.includes('Invalid username or password')) {
        setError('Invalid email or password. Please try again.');
      } else if (error.message.includes('verify your email')) {
        setError('Please verify your email before logging in. Check your inbox for a verification code.');
      } else {
        setError(error.message || 'Failed to login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { handleLogin, error, isLoading };
}

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { handleLogin, error, isLoading } = useHandleLogin(dispatch);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const auth = useSelector((state) => state.auth);

  // Load remembered credentials - with try/catch for Safari
  useEffect(() => {
    try {
      const rememberedEmail = localStorage.getItem('rememberedEmail');
      const rememberedPassword = localStorage.getItem('rememberedPassword');
      
      if (rememberedEmail && rememberedPassword) {
        setEmail(rememberedEmail);
        setRememberMe(true);
      }
    } catch (e) {
      // Continue even if localStorage fails
    }
  }, []);

  // Check if already logged in - improved with token validation
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp > currentTime) {
        navigate('/home');
      } else {
        localStorage.removeItem('token');
        dispatch(setLogout());
      }
    } catch (error) {
      localStorage.removeItem('token');
      dispatch(setLogout());
    }
  }, [dispatch, navigate]);

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/login/forgot-password');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const inputVariants = {
    focused: {
      scale: 1.02,
      boxShadow: "0px 0px 8px rgba(251, 188, 4, 0.3)",
      transition: { type: "spring", stiffness: 300, damping: 15 }
    },
    unfocused: {
      scale: 1,
      boxShadow: "none",
      transition: { type: "spring", stiffness: 300, damping: 15 }
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pri-dark to-sec-dark">
      {/* Animated background elements */}
      <motion.div
        className="absolute top-0 left-0 w-96 h-96 bg-pri-yellow rounded-full mix-blend-multiply filter blur-[128px] opacity-10"
        animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 90, 0],
          x: [-50, 0, -50],
          y: [-50, 0, -50],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.div
        className="absolute bottom-0 right-0 w-96 h-96 bg-pri-green rounded-full mix-blend-multiply filter blur-[128px] opacity-10"
        animate={{
          scale: [1.2, 1, 1.2],
          rotate: [90, 0, 90],
          x: [50, 0, 50],
          y: [50, 0, 50],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "linear"
        }}
      />

      <div className="relative min-h-screen flex items-center justify-center py-2 sm:py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-md w-full space-y-6 sm:space-y-8"
        >
          {/* Logo and Title Section */}
          <motion.div variants={itemVariants} className="text-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mx-auto w-16 sm:w-20 h-16 sm:h-20 relative cursor-pointer"
              onClick={() => navigate('/')}
            >
              <img
                className="h-16 w-auto mx-auto"
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                alt="Jingleez"
              />
              <motion.div
                className="absolute inset-0 bg-soft-pink rounded-full opacity-20 blur-md"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.3, 0.2],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>
            <h2 className="mt-6 text-3xl sm:text-4xl font-extrabold text-soft-pink">
              Welcome Back
            </h2>
            <p className="mt-2 text-sm text-pri-gray">
              Sign in to your Jingleez account
            </p>
            <p className="mt-2 text-sm text-pri-gray">
              Not a member?{' '}
              <Link to="/signup" className="font-medium text-soft-pink hover:text-mint-green transition-colors duration-300">
                Sign Up
              </Link>
            </p>
          </motion.div>

          {/* Login Form Section */}
          <motion.div 
            variants={itemVariants} 
            className="relative overflow-hidden rounded-2xl p-8 
                     bg-gradient-to-br from-soft-pink/10 to-soft-pink/5 
                     backdrop-blur-sm shadow-lg border border-white/10
                     transform-gpu"
            style={{
              transform: 'perspective(1000px) rotateX(2deg)',
            }}
          >
            {/* Glass-like highlight effect at the top */}
            <div className="absolute top-0 left-0 right-0 h-px bg-white/20"></div>
            
            <motion.div variants={itemVariants}>
              <form onSubmit={handleLogin} className="space-y-6">
                {/* Error message */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="bg-red-500/10 text-red-500 p-3 rounded-lg text-sm"
                    >
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Email Field */}
                <motion.div 
                  variants={itemVariants} 
                  className="relative"
                >
                  <motion.div
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    animate={{ color: focusedInput === 'email' ? "#eec9d9" : "#A0AEC0" }}
                  >
                    <FaEnvelope className="h-5 w-5" />
                  </motion.div>
                  <motion.input
                    variants={inputVariants}
                    animate={focusedInput === 'email' ? 'focused' : 'unfocused'}
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedInput('email')}
                    onBlur={() => setFocusedInput(null)}
                    required
                    className="block w-full pl-10 pr-10 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                    placeholder="Email address"
                  />
                </motion.div>

                {/* Password Field */}
                <motion.div 
                  variants={itemVariants} 
                  className="relative"
                >
                  <motion.div
                    className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    animate={{ color: focusedInput === 'password' ? "#eec9d9" : "#A0AEC0" }}
                  >
                    <FaLock className="h-5 w-5" />
                  </motion.div>
                  <motion.input
                    variants={inputVariants}
                    animate={focusedInput === 'password' ? 'focused' : 'unfocused'}
                    type={showPassword ? "text" : "password"}
                    name="password"
                    required
                    onFocus={() => setFocusedInput('password')}
                    onBlur={() => setFocusedInput(null)}
                    className="block w-full pl-10 pr-10 py-3 bg-pri-dark/50 border border-pri-gray/30 rounded-xl text-pri-white placeholder-pri-gray focus:ring-soft-pink focus:border-soft-pink"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FaEyeSlash className="h-5 w-5 text-pri-gray" /> : <FaEye className="h-5 w-5 text-pri-gray" />}
                  </button>
                </motion.div>

                {/* Remember me & Forgot password */}
                <motion.div variants={itemVariants} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 bg-pri-dark border-pri-gray text-pri-dark focus:ring-soft-pink rounded"
                    />
                    <label htmlFor="rememberMe" className="ml-2 block text-sm text-pri-gray">
                      Remember me
                    </label>
                  </div>
                  <a
                    href="#"
                    onClick={handleForgotPassword}
                    className="text-sm font-medium text-soft-pink hover:text-mint-green transition-colors"
                  >
                    Forgot password?
                  </a>
                </motion.div>

                {/* Submit Button */}
                <motion.button
                  variants={itemVariants}
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-full shadow-sm text-pri-dark bg-mint-green hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-soft-pink transition-colors duration-200 relative overflow-hidden"
                >
                  {isLoading ? (
                    <motion.div
                      className="w-6 h-6 border-2 border-t-transparent border-pri-dark rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                  ) : (
                    "Sign In"
                  )}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
